/**
 * Incoming Call Dialog
 *
 * Modal overlay shown when someone calls the current user:
 * - Caller name/avatar
 * - Accept (green) / Decline (red) buttons
 * - Ringtone loop on mount, stops on dismiss
 * - Auto-dismiss after 30s timeout
 */

import React, { useEffect, useRef } from 'react';
import { useVoice, type IncomingCall } from '@/contexts/VoiceContext';
import { soundService, type SoundType } from '@/services/sound.service';
import { useUserSettings } from '@/hooks/useUserSettings';
import { Phone, PhoneOff } from 'lucide-react';
import Avatar from '@/components/common/Avatar';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface IncomingCallDialogProps {
  incomingCall: IncomingCall;
}

export const IncomingCallDialog: React.FC<IncomingCallDialogProps> = ({ incomingCall }) => {
  const { acceptCall, declineCall } = useVoice();
  const { data: userSettings } = useUserSettings();
  const ringtoneRef = useRef<{ stop: () => void } | null>(null);

  // Start ringtone on mount, stop on unmount
  useEffect(() => {
    const ringtoneSound = (userSettings?.call_ringtone || 'callRingtone') as SoundType;
    const ringtoneVolume = userSettings?.call_ringtone_volume ?? 70;

    ringtoneRef.current = soundService.playRingtone(ringtoneSound, ringtoneVolume);

    return () => {
      ringtoneRef.current?.stop();
    };
  }, [userSettings?.call_ringtone, userSettings?.call_ringtone_volume]);

  const handleAccept = () => {
    ringtoneRef.current?.stop();
    acceptCall(incomingCall.sessionId);
  };

  const handleDecline = () => {
    ringtoneRef.current?.stop();
    declineCall(incomingCall.sessionId);
  };

  const { dialogRef } = useDialogA11y(handleDecline);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 animate-in fade-in duration-200">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={incomingCall.isGroup ? 'Входящо групово обаждане' : `Входящо обаждане от ${incomingCall.callerName}`}
        tabIndex={-1}
        className="bg-gray-800 text-white rounded-2xl shadow-2xl p-8 w-80 flex flex-col items-center space-y-6 animate-in slide-in-from-bottom-4 duration-300"
      >
        {/* Pulsing ring indicator */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-green-500/30 animate-ping" />
          <Avatar
            user={{ id: incomingCall.callerId, display_name: incomingCall.callerName, avatar_url: incomingCall.callerAvatar ?? null }}
            size="lg"
          />
        </div>

        {/* Caller info */}
        <div className="text-center">
          <p className="text-lg font-semibold">{incomingCall.callerName}</p>
          <p className="text-sm text-gray-400">
            {incomingCall.isGroup ? 'Групово обаждане...' : 'Обаждане...'}
          </p>
        </div>

        {/* Accept / Decline buttons */}
        <div className="flex items-center justify-center gap-8">
          <button
            onClick={handleDecline}
            className="p-4 rounded-full bg-red-600 hover:bg-red-700 transition-colors shadow-lg"
            title="Отхвърли"
            aria-label="Отхвърли обаждането"
          >
            <PhoneOff className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={handleAccept}
            className="p-4 rounded-full bg-green-600 hover:bg-green-700 transition-colors shadow-lg animate-pulse"
            title="Приеми"
            aria-label="Приеми обаждането"
          >
            <Phone className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};
